import React, { useState, useEffect } from 'react';
import { tenantApi } from '../services/tenantApi';
import { getStoredAuthMethod } from '../services/authService';
import { getStoredTheme, applyTheme, type Theme } from '../shared/themeUtils';
import { DateTimePreferenceControls } from '../shared/DateTimePreferenceControls';
import { resolveDateTimeSettings } from '../shared/dateTimeFormatting';
import type { HourCycle } from '../shared/dateTimePreferences';
import { ShiftReminderPreferencesCard } from './ShiftReminderPreferencesCard';
import './UserDashboard.css';

interface User {
  id: string;
  email: string;
  displayName: string;
  phone?: string;
  timezone?: string;
  dateTimeLocale?: string | null;
  hourCycle?: HourCycle | null;
  roles?: string[];
  notificationPreferences?: NotificationPreferences;
}

interface NotificationPreferences {
  email: boolean;
  sms: boolean;
  push: boolean;
  teams: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  timezone: string;
}

interface UserProfile {
  displayName: string;
  phone: string;
  timezone: string;
  dateTimeLocale: string | null;
  hourCycle: HourCycle | null;
  notificationPreferences: NotificationPreferences;
}

interface ProfileSettingsProps {
  user: User | null;
  onUpdate: (data: Partial<UserProfile>) => Promise<void>;
  onOpenMobileEnrollment?: () => void;
}

const PREF_KEY_SHOW_MY_SCHEDULES_ONLY = 'nog.showMySchedulesOnly';

const getStoredShowMySchedulesOnly = (): boolean => {
  try {
    const value = localStorage.getItem(PREF_KEY_SHOW_MY_SCHEDULES_ONLY);
    if (value == null) return true;
    return value === '1';
  } catch {
    return true;
  }
};

export const ProfileSettings: React.FC<ProfileSettingsProps> = ({ user, onUpdate, onOpenMobileEnrollment }) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications' | 'security' | 'appearance'>('profile');
  const [theme, setTheme] = useState<Theme>(getStoredTheme);
  const [showMySchedulesOnly, setShowMySchedulesOnly] = useState<boolean>(getStoredShowMySchedulesOnly);

  const handleThemeChange = (newTheme: Theme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
  };

  const handleShowMySchedulesOnlyChange = (enabled: boolean) => {
    setShowMySchedulesOnly(enabled);
    try {
      localStorage.setItem(PREF_KEY_SHOW_MY_SCHEDULES_ONLY, enabled ? '1' : '0');
      window.dispatchEvent(new CustomEvent('nog:settings:changed', { detail: { showMySchedulesOnly: enabled } }));
    } catch {
      // ignore storage errors
    }
  };

  // Apply stored theme on mount
  useEffect(() => {
    applyTheme(theme);
  }, []);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const authMethod = getStoredAuthMethod();
  const authMethodTitle = authMethod === 'sso' ? 'Microsoft Entra ID (SSO)' : 'Magic Link Authentication';
  const authMethodDescription = authMethod === 'sso'
    ? "You're signed in via Microsoft single sign-on"
    : "You're signed in via magic link sent to your email";

  const [profileData, setProfileData] = useState<UserProfile>({
    displayName: user?.displayName || '',
    phone: user?.phone || '',
    timezone: resolveDateTimeSettings(user || {}).timezone,
    dateTimeLocale: user?.dateTimeLocale || null,
    hourCycle: user?.hourCycle || null,
    notificationPreferences: user?.notificationPreferences || {
      email: true,
      sms: true,
      push: true,
      teams: true,
      quietHoursEnabled: false,
      quietHoursStart: '22:00',
      quietHoursEnd: '08:00',
      timezone: user?.timezone || 'UTC',
    },
  });

  useEffect(() => {
    setProfileData(previous => ({
      ...previous,
      displayName: user?.displayName || '',
      phone: user?.phone || '',
      timezone: resolveDateTimeSettings(user || {}).timezone,
      dateTimeLocale: user?.dateTimeLocale || null,
      hourCycle: user?.hourCycle || null,
    }));
  }, [user]);

  // Load persisted notification preferences from the API on mount
  useEffect(() => {
    loadNotificationPreferences();
  }, []);

  const loadNotificationPreferences = async () => {
    try {
      const prefs = await tenantApi.getUserNotificationPreferences();
      if (prefs && prefs.length > 0) {
        // Convert API prefs array into the local NotificationPreferences shape
        const prefMap: Record<string, boolean> = {};
        for (const p of prefs) {
          prefMap[p.method.toLowerCase()] = p.isEnabled;
        }
        setProfileData(prev => ({
          ...prev,
          notificationPreferences: {
            ...prev.notificationPreferences,
            email: prefMap['email'] ?? prev.notificationPreferences.email,
            sms: prefMap['sms'] ?? prev.notificationPreferences.sms,
            teams: prefMap['teams'] ?? prev.notificationPreferences.teams,
            push: prefMap['push'] ?? prev.notificationPreferences.push,
          },
        }));
      }
    } catch (err) {
      // Silently ignore — user may not have prefs yet
      console.debug('No notification preferences loaded:', err);
    }
  };

  const saveProfileAndNotificationChanges = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      // Save profile data (displayName, phone, timezone) via existing endpoint
      await onUpdate(profileData);

      // Also persist notification preferences via the new API
      const np = profileData.notificationPreferences;
      const prefsPayload = [
        { method: 'Email', priority: 1, isEnabled: np.email },
        { method: 'SMS', priority: 2, isEnabled: np.sms },
        { method: 'Teams', priority: 3, isEnabled: np.teams },
        { method: 'Push', priority: 4, isEnabled: np.push },
      ];
      await tenantApi.updateUserNotificationPreferences(prefsPayload);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (activeTab === 'appearance') {
      // Appearance changes (theme + schedule filter) are client-side and apply immediately.
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
      return;
    }

    await saveProfileAndNotificationChanges();
  };

  const timezones = [
    'UTC',
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Brisbane',
    'Australia/Perth',
    'Australia/Adelaide',
    'Pacific/Auckland',
    'America/Toronto',
    'America/New_York',
    'America/Los_Angeles',
    'America/Chicago',
    'Europe/London',
    'Europe/Amsterdam',
    'Europe/Paris',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Asia/Hong_Kong',
  ];

  return (
    <div className="profile-settings">
      <div className="profile-tabs">
        <button
          className={`profile-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button
          className={`profile-tab ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          Notifications
        </button>
        <button
          className={`profile-tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          Security
        </button>
        <button
          className={`profile-tab ${activeTab === 'appearance' ? 'active' : ''}`}
          onClick={() => setActiveTab('appearance')}
        >
          Appearance
        </button>
      </div>

      <div className="profile-content">
        {success && (
          <div className="success-banner">Settings saved successfully!</div>
        )}
        {error && (
          <div className="error-banner">{error}</div>
        )}

        {activeTab === 'profile' && (
          <div className="profile-section">
            <h2>Profile Settings</h2>
            
            <div className="avatar-section">
              <div className="avatar">
                {profileData.displayName?.charAt(0).toUpperCase() || '?'}
              </div>
              <div className="avatar-info">
                <p className="user-email">{user?.email}</p>
                <p className="user-roles">
                  {user?.roles?.join(', ') || 'User'}
                </p>
              </div>
            </div>

            <div className="form-section">
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={profileData.displayName}
                  onChange={(e) => setProfileData({ ...profileData, displayName: e.target.value })}
                  placeholder="Your display name"
                />
              </div>

              <div className="form-group">
                <label>Phone Number (for SMS alerts)</label>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  placeholder="+61 400 000 000"
                />
              </div>

              <div className="form-group">
                <label>Timezone</label>
                <select
                  value={profileData.timezone}
                  onChange={(e) => setProfileData({ 
                    ...profileData, 
                    timezone: e.target.value,
                    notificationPreferences: {
                      ...profileData.notificationPreferences,
                      timezone: e.target.value,
                    }
                  })}
                >
                  {Array.from(new Set([...timezones, profileData.timezone])).map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
                <span className="help-text">Used for on-call schedules and quiet hours</span>
              </div>
              <DateTimePreferenceControls value={profileData} onChange={preferences => setProfileData(previous => ({ ...previous, ...preferences }))} />
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="profile-section">
            <h2>Notification Preferences</h2>

            <div className="form-section">
              <h3>Notification Channels</h3>
              <p className="section-description">
                Choose how you want to receive alert notifications when you are on-call or directly targeted
              </p>

              <div className="toggle-group">
                <label className="toggle-item">
                  <span className="toggle-info">
                    <strong>Email</strong>
                    <small>Receive alerts via email</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={profileData.notificationPreferences.email}
                    onChange={(e) => setProfileData({
                      ...profileData,
                      notificationPreferences: {
                        ...profileData.notificationPreferences,
                        email: e.target.checked,
                      }
                    })}
                  />
                  <span className="toggle-slider"></span>
                </label>

                <label className="toggle-item">
                  <span className="toggle-info">
                    <strong>SMS</strong>
                    <small>Receive critical alerts via SMS</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={profileData.notificationPreferences.sms}
                    onChange={(e) => setProfileData({
                      ...profileData,
                      notificationPreferences: {
                        ...profileData.notificationPreferences,
                        sms: e.target.checked,
                      }
                    })}
                  />
                  <span className="toggle-slider"></span>
                </label>

                <label className="toggle-item">
                  <span className="toggle-info">
                    <strong>Microsoft Teams</strong>
                    <small>Receive notifications in Teams</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={profileData.notificationPreferences.teams}
                    onChange={(e) => setProfileData({
                      ...profileData,
                      notificationPreferences: {
                        ...profileData.notificationPreferences,
                        teams: e.target.checked,
                      }
                    })}
                  />
                  <span className="toggle-slider"></span>
                </label>

                <label className="toggle-item">
                  <span className="toggle-info">
                    <strong>Push Notifications</strong>
                    <small>Browser push notifications</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={profileData.notificationPreferences.push}
                    onChange={(e) => setProfileData({
                      ...profileData,
                      notificationPreferences: {
                        ...profileData.notificationPreferences,
                        push: e.target.checked,
                      }
                    })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <h3>Quiet Hours</h3>
              <p className="section-description">
                Pause non-critical notifications during set hours
              </p>

              <div className="toggle-group">
                <label className="toggle-item">
                  <span className="toggle-info">
                    <strong>Enable Quiet Hours</strong>
                    <small>Silence notifications during specified hours</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={profileData.notificationPreferences.quietHoursEnabled}
                    onChange={(e) => setProfileData({
                      ...profileData,
                      notificationPreferences: {
                        ...profileData.notificationPreferences,
                        quietHoursEnabled: e.target.checked,
                      }
                    })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {profileData.notificationPreferences.quietHoursEnabled && (
                <div className="quiet-hours-config">
                  <div className="form-group">
                    <label>Start Time</label>
                    <input
                      type="time"
                      value={profileData.notificationPreferences.quietHoursStart || '22:00'}
                      onChange={(e) => setProfileData({
                        ...profileData,
                        notificationPreferences: {
                          ...profileData.notificationPreferences,
                          quietHoursStart: e.target.value,
                        }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>End Time</label>
                    <input
                      type="time"
                      value={profileData.notificationPreferences.quietHoursEnd || '08:00'}
                      onChange={(e) => setProfileData({
                        ...profileData,
                        notificationPreferences: {
                          ...profileData.notificationPreferences,
                          quietHoursEnd: e.target.value,
                        }
                      })}
                    />
                  </div>
                  <p className="help-text">
                    Critical alerts (P1/P2) will still come through during quiet hours.
                  </p>
                </div>
              )}

              <h3>Alert Severity</h3>
              <p className="section-description">
                Minimum severity level for notifications
              </p>
              <div className="severity-options">
                {['Critical', 'High', 'Medium', 'Low'].map((severity) => (
                  <label key={severity} className="radio-item">
                    <input
                      type="radio"
                      name="minSeverity"
                      value={severity}
                      checked={profileData.notificationPreferences['minSeverity' as keyof NotificationPreferences] === severity}
                      onChange={() => {}}
                    />
                    <span className={`severity-badge ${severity.toLowerCase()}`}>{severity}</span>
                  </label>
                ))}
              </div>
            </div>

            <ShiftReminderPreferencesCard key={user?.id ?? 'anonymous'} userId={user?.id} />
          </div>
        )}

        {activeTab === 'security' && (
          <div className="profile-section">
            <h2>Security Settings</h2>

            <div className="form-section">
              <h3>Authentication</h3>
              <div className="security-item">
                <div className="security-info">
                  <strong>{authMethodTitle}</strong>
                  <p>{authMethodDescription}</p>
                </div>
                <span className="status-enabled">Enabled</span>
              </div>

              {user?.notificationPreferences?.sms && (
                <div className="security-item">
                  <div className="security-info">
                    <strong>SMS Two-Factor Authentication</strong>
                    <p>Receive a code via SMS when signing in from new devices</p>
                  </div>
                  <label className="toggle-small">
                    <input type="checkbox" />
                    <span className="toggle-slider-small"></span>
                  </label>
                </div>
              )}

              <h3>Sessions</h3>
              <p className="section-description">
                Active sessions where you're logged in
              </p>
              <div className="session-list">
                <div className="session-item current">
                  <div className="session-info">
                    <strong>Current Session</strong>
                    <p>Active now</p>
                  </div>
                  <span className="session-badge current">This device</span>
                </div>
              </div>

              <button className="btn-secondary danger">
                Sign Out All Other Sessions
              </button>

              <h3 style={{ marginTop: '1.5rem' }}>Mobile Devices</h3>
              <p className="section-description">
                Use tenant mobile enrollment QR to register this app on a new device.
              </p>
              <button className="btn-secondary" onClick={() => onOpenMobileEnrollment?.()}>
                Open tenant mobile enrollment
              </button>
            </div>
          </div>
        )}

        {activeTab === 'appearance' && (
          <div className="profile-section">
            <h2>Appearance</h2>
            <div className="form-section">
              <h3>Theme</h3>
              <p className="section-description">Choose your preferred colour scheme</p>
              <div className="theme-options">
                <button
                  className={`theme-option ${theme === 'light' ? 'active' : ''}`}
                  onClick={() => handleThemeChange('light')}
                >
                  <span className="theme-icon">☀️</span>
                  <span>Light</span>
                </button>
                <button
                  className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
                  onClick={() => handleThemeChange('dark')}
                >
                  <span className="theme-icon">🌙</span>
                  <span>Dark</span>
                </button>
              </div>

              <h3 style={{ marginTop: '1.5rem' }}>Schedule View</h3>
              <p className="section-description">Control whether the On-Call page shows only schedules you belong to.</p>
              <div className="toggle-group">
                <label className="toggle-item">
                  <span className="toggle-info">
                    <strong>My schedules only</strong>
                    <small>When enabled, only schedules where you are a member are shown.</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={showMySchedulesOnly}
                    onChange={(e) => handleShowMySchedulesOnlyChange(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        )}

        <div className="profile-actions">
          <button onClick={handleSave} className="btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
